import { useCallback, useState } from "react";

// Note: All Stripe operations that require a secret key are handled by the backend API.
// The frontend only uses the publishable key via @stripe/stripe-js for client-side operations.

/**
 * 1 MONTH FREE COUPON IMPLEMENTATION
 *
 * This hook automatically applies a "1_month_free" coupon to new user signups.
 * The coupon provides $19.99 off for the first month of any subscription.
 *
 * Coupon Details:
 * - ID: "fyHAX6wK"
 * - Promotion Code: "ARTIFACT_1FREE" (optional for manual entry)
 * - Duration: Once
 * - Discount: $19.99 off
 *
 * Implementation:
 * - createSubscriptionCheckout: Includes coupon for new users by default
 * - createSubscriptionCheckoutWith1MonthFree: Forces the coupon application
 * - Backend endpoint: /api/stripe/create-subscription-14day-trial
 *
 * The coupon is automatically applied during checkout session creation,
 * ensuring all new users get 1 month free without manual intervention.
 */

/**
 * Parse JSON response safely
 * @param {Response} response - Fetch response
 * @returns {Promise<Object>} - Parsed JSON data or null
 */
const safelyParseJSON = async (response) => {
  try {
    const text = await response.text();
    return text ? JSON.parse(text) : null;
  } catch (error) {
    console.error("Error parsing JSON response:", error);
    return null;
  }
};

/**
 * Custom hook for Stripe operations
 */
export const useStripe = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Create a Stripe checkout session
   * @param {Object} options - Checkout session options
   * @returns {Promise<Object>} - Checkout session data
   */
  const createCheckoutSession = useCallback(async (options = {}) => {
    try {
      setLoading(true);
      setError(null);

      const {
        lineItems = [],
        mode = "payment",
        successUrl,
        cancelUrl,
        returnUrl,
        embedded = false,
        customerEmail,
      } = options;

      const response = await fetch("/api/stripe/checkout", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          lineItems,
          mode,
          successUrl,
          cancelUrl,
          returnUrl,
          embedded,
          customerEmail,
        }),
      });

      // Log the raw response for debugging
      const responseText = await response.text();

      if (!response.ok) {
        // Try to parse error response if possible
        let errorMessage = "Failed to create checkout session";
        try {
          const errorData = responseText ? JSON.parse(responseText) : null;
          errorMessage = errorData?.message || errorData?.error || errorMessage;
        } catch (e) {
          console.error("Failed to parse error response:", e);
        }
        throw new Error(errorMessage);
      }

      // Try to parse the success response
      let data;
      try {
        data = responseText ? JSON.parse(responseText) : null;
      } catch (e) {
        console.error("Failed to parse success response:", e);
        throw new Error("Invalid response from server");
      }

      // Validate the response data
      if (!data) {
        throw new Error("No data received from server");
      }

      if (embedded && !data.clientSecret) {
        throw new Error("No client secret received for embedded checkout");
      }

      if (!embedded && !data.url) {
        throw new Error("No checkout URL received for redirect checkout");
      }

      return embedded
        ? { clientSecret: data.clientSecret }
        : { sessionId: data.id, url: data.url };
    } catch (err) {
      console.error("Error creating checkout session:", err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get the status of a checkout session
   * @param {string} sessionId - The ID of the checkout session
   * @returns {Object} - Session status data
   */
  const getSessionStatus = useCallback(async (sessionId) => {
    try {
      setLoading(true);
      setError(null);

      console.log(
        "🔍 [useStripe] getSessionStatus called with sessionId:",
        sessionId
      );

      const response = await fetch(
        `/api/stripe/session-status?session_id=${sessionId}`
      );

      console.log("📊 [useStripe] API response status:", response.status);
      console.log("📊 [useStripe] API response ok:", response.ok);
      console.log(
        "📊 [useStripe] API response headers:",
        Object.fromEntries(response.headers.entries())
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error("❌ [useStripe] API error response text:", errorText);

        const error = (await safelyParseJSON(response)) || {
          message: response.statusText,
        };
        console.error("❌ [useStripe] Parsed error:", error);
        throw new Error(error.message || "Failed to get session status");
      }

      const data = await safelyParseJSON(response);
      console.log("📊 [useStripe] Raw response data:", data);
      console.log(
        "📊 [useStripe] Subscription in response:",
        data?.subscription
      );
      console.log(
        "📊 [useStripe] Subscription ID in response:",
        data?.subscription_id || data?.subscription?.id
      );
      console.log(
        "📊 [useStripe] Customer in response:",
        data?.customer || data?.customer_id
      );

      if (!data) {
        console.error("❌ [useStripe] Empty response from server");
        throw new Error("Empty response from server");
      }

      // Log key subscription fields
      if (data.subscription) {
        console.log("✅ [useStripe] Subscription object found:", {
          id: data.subscription.id,
          status: data.subscription.status,
          customer: data.subscription.customer,
          current_period_start: data.subscription.current_period_start,
          current_period_end: data.subscription.current_period_end,
        });
      } else {
        console.warn("⚠️ [useStripe] No subscription object in response");
      }

      if (data.subscription_id) {
        console.log(
          "✅ [useStripe] Subscription ID found:",
          data.subscription_id
        );
      } else {
        console.warn("⚠️ [useStripe] No subscription_id field in response");
      }

      console.log("✅ [useStripe] getSessionStatus completed successfully");
      return data;
    } catch (err) {
      console.error("❌ [useStripe] Error getting session status:", err);
      console.error("❌ [useStripe] Error message:", err.message);
      console.error("❌ [useStripe] Error stack:", err.stack);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Create a payment intent
   * @param {Object} options - Payment intent options
   * @returns {Object} - Payment intent data
   */
  const createPaymentIntent = useCallback(
    async ({
      amount,
      currency = "usd",
      paymentMethodTypes = ["card"],
      description,
      metadata,
    }) => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch("/api/stripe/payment-intent", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            amount,
            currency,
            paymentMethodTypes,
            description,
            metadata,
          }),
        });

        if (!response.ok) {
          const error = (await safelyParseJSON(response)) || {
            message: response.statusText,
          };
          throw new Error(error.message || "Failed to create payment intent");
        }

        const data = await safelyParseJSON(response);
        if (!data) {
          throw new Error("Empty response from server");
        }

        return {
          clientSecret: data.clientSecret,
          paymentIntentId: data.paymentIntentId,
        };
      } catch (err) {
        console.error("Error creating payment intent:", err);
        setError(err.message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // Note: Webhook handling is done on the backend only.
  // See /api/stripe/webhook endpoint for webhook processing.

  // Note: Use getSubscriptionPlans() which fetches from /api/stripe/plans endpoint.
  // Direct Stripe SDK price listing is handled by the backend.

  /**
   * Map a plan ID and interval to a Stripe price ID
   * @param {string} plan - The plan ID (e.g., 'pro', 'enterprise')
   * @param {string} interval - The billing interval ('month' or 'year')
   * @returns {string|null} - The corresponding Stripe price ID
   */
  const getPriceIdForPlan = useCallback((plan, interval) => {
    // Use environment variables for price IDs
    const priceMap = {
      free: {
        month: import.meta.env.VITE_STRIPE_PRICE_FREE,
        year: import.meta.env.VITE_STRIPE_PRICE_FREE_ANNUAL,
      },
      pro: {
        month: import.meta.env.VITE_STRIPE_PRICE_PRO,
        year: import.meta.env.VITE_STRIPE_PRICE_PRO_ANNUAL,
      },
      business: {
        month: import.meta.env.VITE_STRIPE_PRICE_BUSINESS,
        year: import.meta.env.VITE_STRIPE_PRICE_BUSINESS_ANNUAL,
      },
      team: {
        month: import.meta.env.VITE_STRIPE_PRICE_TEAM,
        year: import.meta.env.VITE_STRIPE_PRICE_TEAM_ANNUAL,
      },
    };

    return priceMap[plan]?.[interval] || null;
  }, []);

  /**
   * Check subscription status for a user
   * @param {string} userId - The user ID
   * @returns {Promise<Object>} - Subscription status data
   */
  const checkSubscriptionStatus = useCallback(async (userId) => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(
        `/api/stripe/subscription-status?user_id=${userId}`
      );

      if (!response.ok) {
        const error = (await safelyParseJSON(response)) || {
          message: response.statusText,
        };
        throw new Error(error.message || "Failed to check subscription status");
      }

      const data = await safelyParseJSON(response);
      if (!data) {
        throw new Error("Empty response from server");
      }

      return data;
    } catch (err) {
      console.error("Error checking subscription status:", err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Cancel a subscription
   * @param {string} subscriptionId - The subscription ID to cancel
   * @param {Object} options - Cancellation options
   * @param {boolean} options.cancelAtPeriodEnd - Whether to cancel at the end of the current period
   * @param {boolean} options.prorate - Whether to prorate the cancellation
   * @param {boolean} options.invoiceNow - Whether to immediately invoice for prorations
   * @param {Object} options.cancellationDetails - Details about the cancellation
   * @returns {Promise<Object>} - Cancellation result
   */
  const cancelSubscription = useCallback(
    async (subscriptionId, options = {}) => {
      try {
        setLoading(true);
        setError(null);

        console.log("🚀 [useStripe] cancelSubscription called with:", {
          subscriptionId,
          options,
        });

        const {
          cancelAtPeriodEnd = false,
          prorate = false,
          invoiceNow = false,
          cancellationDetails = {},
        } = options;

        const requestBody = {
          subscriptionId,
          cancelAtPeriodEnd,
          prorate,
          invoiceNow,
          cancellationDetails,
        };

        console.log(
          "📊 [useStripe] Cancel subscription request body:",
          requestBody
        );

        const response = await fetch("/api/stripe/cancel-subscription", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        });

        console.log(
          "📊 [useStripe] Cancel subscription response status:",
          response.status
        );
        console.log(
          "📊 [useStripe] Cancel subscription response ok:",
          response.ok
        );

        if (!response.ok) {
          const errorText = await response.text();
          console.error(
            "❌ [useStripe] Cancel subscription error response:",
            errorText
          );

          const error = (await safelyParseJSON(response)) || {
            message: response.statusText,
          };
          console.error(
            "❌ [useStripe] Parsed cancel subscription error:",
            error
          );
          throw new Error(error.message || "Failed to cancel subscription");
        }

        const data = await safelyParseJSON(response);
        console.log("📊 [useStripe] Cancel subscription response data:", data);

        if (!data) {
          console.error(
            "❌ [useStripe] Empty response from cancel subscription"
          );
          throw new Error("Empty response from server");
        }

        console.log("✅ [useStripe] Subscription canceled successfully");
        return data;
      } catch (err) {
        console.error("❌ [useStripe] Error canceling subscription:", err);
        console.error("❌ [useStripe] Error message:", err.message);
        console.error("❌ [useStripe] Error stack:", err.stack);
        setError(err.message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * Reactivate a subscription that was scheduled for cancellation
   * @param {string} subscriptionId - The subscription ID to reactivate
   * @returns {Promise<Object>} - Reactivation result
   */
  const reactivateSubscription = useCallback(async (subscriptionId) => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch("/api/stripe/reactivate-subscription", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ subscriptionId }),
      });

      if (!response.ok) {
        const error = (await safelyParseJSON(response)) || {
          message: response.statusText,
        };
        throw new Error(error.message || "Failed to reactivate subscription");
      }

      const data = await safelyParseJSON(response);
      if (!data) {
        throw new Error("Empty response from server");
      }

      return data;
    } catch (err) {
      console.error("Error reactivating subscription:", err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Schedule a subscription to cancel at a specific future date
   * @param {string} subscriptionId - The subscription ID to schedule for cancellation
   * @param {number|string} cancelAt - Unix timestamp or ISO date string when to cancel
   * @returns {Promise<Object>} - Scheduling result
   */
  const scheduleSubscriptionCancellation = useCallback(
    async (subscriptionId, cancelAt) => {
      try {
        setLoading(true);
        setError(null);

        const requestBody = { subscriptionId };

        // Handle both Unix timestamp and ISO date string
        if (typeof cancelAt === "string") {
          requestBody.cancelDate = cancelAt;
        } else {
          requestBody.cancelAt = cancelAt;
        }

        const response = await fetch(
          "/api/stripe/schedule-subscription-cancellation",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody),
          }
        );

        if (!response.ok) {
          const error = (await safelyParseJSON(response)) || {
            message: response.statusText,
          };
          throw new Error(
            error.message || "Failed to schedule subscription cancellation"
          );
        }

        const data = await safelyParseJSON(response);
        if (!data) {
          throw new Error("Empty response from server");
        }

        return data;
      } catch (err) {
        console.error("Error scheduling subscription cancellation:", err);
        setError(err.message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * Get customer subscriptions by customer ID
   * @param {string} customerId - The Stripe customer ID
   * @returns {Promise<Object>} - Customer subscriptions
   */
  const getCustomerSubscriptions = useCallback(async (customerId) => {
    try {
      setLoading(true);
      setError(null);

      console.log(
        "🔍 [useStripe] getCustomerSubscriptions called with customerId:",
        customerId
      );

      // Validate customer ID format
      if (
        !customerId ||
        typeof customerId !== "string" ||
        !customerId.startsWith("cus_")
      ) {
        throw new Error(`Invalid customer ID format: ${customerId}`);
      }

      const response = await fetch(
        `/api/stripe/customer-subscriptions/${customerId}`
      );

      console.log(
        "📊 [useStripe] Customer subscriptions response status:",
        response.status
      );
      console.log(
        "📊 [useStripe] Customer subscriptions response ok:",
        response.ok
      );
      console.log(
        "📊 [useStripe] Customer subscriptions response headers:",
        Object.fromEntries(response.headers.entries())
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error(
          "❌ [useStripe] Customer subscriptions error response:",
          errorText
        );

        let error;
        try {
          error = JSON.parse(errorText);
        } catch (parseError) {
          error = { message: response.statusText };
        }

        console.error(
          "❌ [useStripe] Parsed customer subscriptions error:",
          error
        );
        throw new Error(
          error.message ||
            error.error ||
            `HTTP ${response.status}: ${response.statusText}`
        );
      }

      const responseText = await response.text();
      console.log("📊 [useStripe] Raw response text:", responseText);

      let data;
      try {
        data = responseText ? JSON.parse(responseText) : null;
      } catch (parseError) {
        console.error(
          "❌ [useStripe] Error parsing response JSON:",
          parseError
        );
        throw new Error("Invalid JSON response from server");
      }

      console.log("📊 [useStripe] Customer subscriptions response data:", data);
      console.log("📊 [useStripe] Data type:", typeof data);
      console.log("📊 [useStripe] Data keys:", Object.keys(data || {}));
      console.log(
        "📊 [useStripe] Number of subscriptions found:",
        data?.subscriptions?.length || 0
      );

      if (data?.subscriptions && Array.isArray(data.subscriptions)) {
        data.subscriptions.forEach((sub, index) => {
          console.log(`📊 [useStripe] Subscription ${index + 1}:`, {
            id: sub.id,
            status: sub.status,
            current_period_start: sub.current_period_start,
            current_period_end: sub.current_period_end,
            cancel_at_period_end: sub.cancel_at_period_end,
            created: sub.created,
          });
        });
      } else {
        console.warn(
          "⚠️ [useStripe] No subscriptions array found in response or not an array"
        );
      }

      if (!data) {
        console.error(
          "❌ [useStripe] Empty response from customer subscriptions"
        );
        throw new Error("Empty response from server");
      }

      console.log(
        "✅ [useStripe] Customer subscriptions retrieved successfully"
      );
      return data;
    } catch (err) {
      console.error(
        "❌ [useStripe] Error getting customer subscriptions:",
        err
      );
      console.error("❌ [useStripe] Error message:", err.message);
      console.error("❌ [useStripe] Error stack:", err.stack);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Create a subscription checkout session
   * @param {Object} options - Subscription options
   * @returns {Promise<Object>} - Subscription session data
   */
  const createSubscriptionCheckout = useCallback(async (options) => {
    try {
      setLoading(true);
      setError(null);

      const {
        planId,
        billingInterval,
        successUrl,
        cancelUrl,
        customerEmail,
        allowPromotionCodes,
        isNewUser = true, // Default to true for new signups
        apply1MonthFree = true, // Default to true to apply the 1 month free coupon
      } = options;

      // Prepare the request body with all necessary parameters
      const requestBody = {
        planId: planId, // Backend expects planType not planId
        billingInterval: billingInterval || "month",
        successUrl,
        cancelUrl,
        customerEmail,
        allowPromotionCodes,
        // Add the 1 month free coupon for new users
        apply1MonthFree: apply1MonthFree && isNewUser,
        couponId: apply1MonthFree && isNewUser ? "fyHAX6wK" : undefined,
      };

      const response = await fetch("/api/stripe/create-subscription", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const error = (await safelyParseJSON(response)) || {
          message: response.statusText,
        };
        throw new Error(
          error.error || error.message || "Failed to create subscription"
        );
      }

      const data = await safelyParseJSON(response);
      if (!data) {
        throw new Error("Empty response from server");
      }

      return data;
    } catch (err) {
      console.error("Error creating subscription:", err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Create a subscription checkout session with 1 month free
   * @param {Object} options - Subscription options
   * @returns {Promise<Object>} - Subscription session data
   */
  const createSubscriptionCheckoutWith1MonthFree = useCallback(
    async (options) => {
      try {
        setLoading(true);
        setError(null);

        console.log(
          "🚀 [useStripe] createSubscriptionCheckoutWith1MonthFree called with options:",
          options
        );

        const {
          planId,
          billingInterval,
          successUrl,
          cancelUrl,
          customerEmail,
          allowPromotionCodes = true,
        } = options;

        // Prepare the request body with the 1 month free coupon
        const requestBody = {
          planId: planId,
          billingInterval: billingInterval || "month",
          successUrl,
          cancelUrl,
          customerEmail,
          allowPromotionCodes,
          // Force apply the 1 month free coupon
          apply1MonthFree: true,
          couponId: "fyHAX6wK",
          metadata: {
            promotion_applied: "1_month_free",
            new_user_signup: "true",
          },
        };

        console.log("📊 [useStripe] Request body:", requestBody);

        const response = await fetch(
          "/api/stripe/create-subscription-14day-trial",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody),
          }
        );

        console.log(
          "📊 [useStripe] Subscription creation response status:",
          response.status
        );
        console.log(
          "📊 [useStripe] Subscription creation response ok:",
          response.ok
        );

        if (!response.ok) {
          const errorText = await response.text();
          console.error(
            "❌ [useStripe] Subscription creation error response:",
            errorText
          );

          const error = (await safelyParseJSON(response)) || {
            message: response.statusText,
          };
          console.error(
            "❌ [useStripe] Parsed subscription creation error:",
            error
          );
          throw new Error(
            error.error ||
              error.message ||
              "Failed to create subscription with 1 month free"
          );
        }

        const data = await safelyParseJSON(response);
        console.log(
          "📊 [useStripe] Subscription creation response data:",
          data
        );
        console.log("📊 [useStripe] Checkout URL:", data?.url);
        console.log("📊 [useStripe] Session ID:", data?.sessionId);

        if (!data) {
          console.error(
            "❌ [useStripe] Empty response from subscription creation"
          );
          throw new Error("Empty response from server");
        }

        console.log(
          "✅ [useStripe] Subscription checkout created successfully"
        );
        return data;
      } catch (err) {
        console.error(
          "❌ [useStripe] Error creating subscription with 1 month free:",
          err
        );
        console.error("❌ [useStripe] Error message:", err.message);
        console.error("❌ [useStripe] Error stack:", err.stack);
        setError(err.message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * Get available subscription plans
   * @returns {Promise<Object>} - Available plans
   */
  const getSubscriptionPlans = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch("/api/stripe/plans");

      if (!response.ok) {
        const error = (await safelyParseJSON(response)) || {
          message: response.statusText,
        };
        throw new Error(
          error.error || error.message || "Failed to fetch subscription plans"
        );
      }

      const data = await safelyParseJSON(response);
      if (!data) {
        throw new Error("Empty response from server");
      }

      return data;
    } catch (err) {
      console.error("Error fetching subscription plans:", err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Check if subscription has been processed via webhook
   * @param {string} sessionId - The checkout session ID
   * @returns {Promise<Object>} - Subscription processing status
   */
  const checkSubscriptionProcessingStatus = useCallback(async (sessionId) => {
    try {
      setLoading(true);
      setError(null);

      console.log(
        "🔍 [useStripe] Getting subscription data for session:",
        sessionId
      );

      const response = await fetch(
        `/api/stripe/subscription-processing-status?session_id=${sessionId}`
      );

      console.log(
        "📊 [useStripe] Processing status response:",
        response.status
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error("❌ [useStripe] Processing status error:", errorText);
        throw new Error(
          `Failed to check processing status: ${response.status}`
        );
      }

      const data = await safelyParseJSON(response);
      console.log("📊 [useStripe] Processing status data:", data);

      return {
        success: data.success,
        sessionDetails: data.sessionDetails,
        subscriptionData: data.subscriptionData,
        reason: data.reason,
        message: data.message,
      };
    } catch (err) {
      console.error(
        "❌ [useStripe] Error checking subscription processing status:",
        err
      );
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Debug customer subscriptions - get detailed information for troubleshooting
   * @param {string} customerId - The Stripe customer ID
   * @param {string} subscriptionId - Optional subscription ID for detailed info
   * @returns {Promise<Object>} - Debug information
   */
  const debugCustomerSubscriptions = useCallback(
    async (customerId, subscriptionId = null) => {
      try {
        setLoading(true);
        setError(null);

        console.log("🧪 [useStripe] debugCustomerSubscriptions called with:", {
          customerId,
          subscriptionId,
        });

        const response = await fetch(
          "/api/stripe/debug-customer-subscriptions",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ customerId, subscriptionId }),
          }
        );

        if (!response.ok) {
          const errorText = await response.text();
          console.error("❌ [useStripe] Debug error response:", errorText);
          throw new Error(`Debug request failed: ${response.status}`);
        }

        const data = await response.json();
        console.log("🧪 [useStripe] Debug response:", data);
        return data;
      } catch (err) {
        console.error("❌ [useStripe] Error in debug request:", err);
        setError(err.message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * Create a Stripe Customer Portal session
   * @param {string} customerId - The Stripe customer ID
   * @param {string} returnUrl - The URL to redirect back to after the portal session
   * @returns {Promise<Object>} - Portal session data including the URL
   */
  const createPortalSession = useCallback(
    async (customerId, returnUrl = null) => {
      try {
        setLoading(true);
        setError(null);

        if (!customerId) {
          throw new Error("Customer ID is required for portal session");
        }

        // Get API_BASE_URL from environment
        const API_BASE_URL =
          import.meta.env.VITE_API_BASE_URL || "http://localhost:3002";

        const response = await fetch(
          `${API_BASE_URL}/api/stripe/create-customer-portal-session`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              customerId,
              returnUrl: returnUrl || window.location.href,
            }),
          }
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || "Failed to create portal session");
        }

        const data = await response.json();
        return data;
      } catch (err) {
        console.error("Error creating portal session:", err);
        setError(err.message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // Clear error function
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    // State
    loading,
    error,

    // Actions
    createCheckoutSession,
    getSessionStatus,
    createPaymentIntent,
    getPriceIdForPlan,
    checkSubscriptionStatus,
    cancelSubscription,
    reactivateSubscription,
    scheduleSubscriptionCancellation,
    getCustomerSubscriptions,
    createSubscriptionCheckout,
    createSubscriptionCheckoutWith1MonthFree,
    getSubscriptionPlans,
    checkSubscriptionProcessingStatus,
    debugCustomerSubscriptions,
    createPortalSession,
    clearError,
  };
};

export default useStripe;
